import { useEffect, useState } from "react";
import { NodeRunningState } from "./NodeConfiguration";
import { RoundIconButton } from "../../../../components/roundIconButton/round-icon-button";
import { DataNodeModel } from "../../diagram/nodes/DataNodeModel";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { StreamUpdateData, useUpdateStreamMutation } from "../../../../app/apiSlice";
import { setToast } from "../../../../app/appSlice";
import { setTopology,setTopologyData } from "../../editorSlice";

export interface SubProcessToolbarProps {
  node: DataNodeModel;
  onRunningStateChange: (state: NodeRunningState) => void;
}

const SubProcessToolbar = (props: SubProcessToolbarProps) => {

  const dispatch = useAppDispatch();
  const [state, setState] = useState<NodeRunningState>(NodeRunningState.RUNNING);
  const topology = useAppSelector((state) =>  state.editor.current);
  const [currentTopologyData, setCurrentTopologyData] = useState<any[]>();
  const [updateStream] = useUpdateStreamMutation();

  useEffect(() => {
    props.onRunningStateChange && props.onRunningStateChange(state);
  }, [state]);

  useEffect(() => {
    if (topology) {
      try {
        const tData = JSON.parse(topology.data);
        setCurrentTopologyData(tData);
      } catch {
        console.error('Unable to parse topology data');
      }
    }
  }, [topology]);

  useEffect(() => {
    updateRunningState();
  }, [currentTopologyData, props.node]);

  const updateRunningState = () => {
    const nodeData = getDataForNode(props.node);
    if (nodeData) {
      setState(nodeData.meta && nodeData.meta.pause === true ? NodeRunningState.PAUSED : NodeRunningState.RUNNING);
    }
  }

  const onPauseClick = async () => {
    const requestBody = getRequestBody(true, true);
    if (requestBody) {
      try {
        setState(NodeRunningState.STOPPING);
        const newTopology = await updateStream({topology: requestBody}).unwrap();
        if (newTopology) {
          dispatch(setTopology(newTopology));
          dispatch(setTopologyData(newTopology.data));
        }
        dispatch(
          setToast({severity: "success", content: "Mise en pause de la brique réussie", life: 5000})
        );
        setState(NodeRunningState.PAUSED);
      } catch {
        setState(NodeRunningState.RUNNING);
        dispatch(
          setToast({severity: "error", content: "Echec de la mise en pause de la brique", life: 5000})
        );
      }
    }
  }

  const onResumeClick = async () => {
    const requestBody = getRequestBody(false, true);
    if (requestBody) {
      try {
        setState(NodeRunningState.RESUMING);
        const newTopology = await updateStream({topology: requestBody}).unwrap();
        if (newTopology) {
          dispatch(setTopology(newTopology));
          dispatch(setTopologyData(newTopology.data));
        }
        dispatch(
          setToast({severity: "success", content: "Redémarrage de la brique réussie", life: 5000})
        );
        setState(NodeRunningState.RUNNING);
      } catch {
        setState(NodeRunningState.PAUSED);
        dispatch(
          setToast({severity: "error", content: "Echec du redémarrage de la brique", life: 5000})
        );
      }
    }
  }

  const getDataForNode = (node: DataNodeModel): any|undefined => {
    if (node && currentTopologyData) {
      return currentTopologyData.find((n: any) => n.id === node.getID());
    }
    return undefined;
  }

  const getRequestBody = (pause: boolean, update: boolean): StreamUpdateData|undefined => {
    if (topology && currentTopologyData) {
      // Build new topology data
      const newTopologyData = currentTopologyData?.map((nodeData) => {
        if (nodeData.id === props.node.getID()) {
          return {
            ...nodeData,
            meta: {
              ...nodeData.meta,
              pause: pause,
              update: update,
            }
          }
        } else {
          return nodeData;
        }
      });
      // Build request body
      return {
        id: topology?.id,
        graph: topology?.graph,
        data: JSON.stringify(newTopologyData),
      }
    }
    return undefined;
  }

  return (
    <div className="node-play-actions">
      {state === NodeRunningState.RUNNING &&
        <RoundIconButton
          icon="pi pi-pause"
          onClick={onPauseClick}
        />
      }
      {state === NodeRunningState.PAUSED &&
        <RoundIconButton
          icon="pi pi-play"
          onClick={onResumeClick}
        />
      }
      {(state === NodeRunningState.STOPPING || state === NodeRunningState.RESUMING) &&
        <RoundIconButton
          icon={`pi pi-spin pi-spinner`}
        />
      }
    </div>
  );
}

export default SubProcessToolbar;